export interface PdfStructuralLimits {
  fileBytes: number
  indirectObjects: number
  incrementalUpdates: number
  declaredObjectSlots: number
  objectStreams: number
  objectsPerStream: number
  declaredStreamBytes: number
  aggregateDeclaredStreamBytes: number
  dictionaryBytes: number
  nestingDepth: number
}

export class PdfStructuralError extends Error {
  constructor(
    public readonly code: 'invalid' | 'limit',
    message: string,
  ) {
    super(message)
    this.name = 'PdfStructuralError'
  }
}

export interface PdfStructuralStream {
  dataStart: number
  length: number
  filters: string[]
  type?: string
  subtype?: string
  typeIsIndirect: boolean
  subtypeIsIndirect: boolean
  width?: number
  height?: number
}

export interface PdfStructuralInspection {
  streams: PdfStructuralStream[]
}

const DEFAULT_LIMITS: PdfStructuralLimits = {
  fileBytes: 32 * 1024 * 1024,
  indirectObjects: 25_000,
  incrementalUpdates: 16,
  declaredObjectSlots: 100_000,
  objectStreams: 256,
  objectsPerStream: 2_000,
  declaredStreamBytes: 32 * 1024 * 1024,
  aggregateDeclaredStreamBytes: 128 * 1024 * 1024,
  dictionaryBytes: 8_192,
  nestingDepth: 32,
}

type PdfValue =
  | { kind: 'name'; value: string }
  | { kind: 'name-array'; values: string[] }
  | { kind: 'number'; value: number }
  | { kind: 'reference'; objectNumber: number; generation: number }
  | { kind: 'complex' }

interface PdfDictionary {
  end: number
  entries: Map<string, PdfValue>
}

interface Scanner {
  source: string
  position: number
  limits: PdfStructuralLimits
  dictionaryStart: number
  dictionaryExemptStart: number
  exemptBytes: number
}

function isWhitespace(character: string | undefined): boolean {
  return character !== undefined && /[\0\t\n\f\r ]/.test(character)
}

function isDelimiter(character: string | undefined): boolean {
  return character === undefined || isWhitespace(character) || '()<>[]{}/%'.includes(character)
}

function ensureDictionaryBound(scanner: Scanner): void {
  if (
    scanner.position -
      scanner.dictionaryStart -
      (scanner.exemptBytes - scanner.dictionaryExemptStart) >
    scanner.limits.dictionaryBytes
  ) {
    throw new PdfStructuralError(
      'limit',
      `PDF dictionary at byte ${scanner.dictionaryStart} exceeds the preflight scan limit`,
    )
  }
}

function skipTrivia(scanner: Scanner): void {
  while (scanner.position < scanner.source.length) {
    ensureDictionaryBound(scanner)
    if (isWhitespace(scanner.source[scanner.position])) {
      scanner.position += 1
      continue
    }
    if (scanner.source[scanner.position] !== '%') return
    while (
      scanner.position < scanner.source.length &&
      !['\r', '\n'].includes(scanner.source[scanner.position] ?? '')
    ) {
      scanner.position += 1
      ensureDictionaryBound(scanner)
    }
  }
}

function decodeName(source: string, start: number, end: number): string {
  let result = ''
  for (let index = start; index < end; index += 1) {
    if (
      source[index] === '#' &&
      index + 2 < end &&
      /^[\da-f]{2}$/i.test(source.slice(index + 1, index + 3))
    ) {
      result += String.fromCharCode(Number.parseInt(source.slice(index + 1, index + 3), 16))
      index += 2
    } else {
      result += source[index]
    }
  }
  return result
}

function readName(scanner: Scanner): string {
  if (scanner.source[scanner.position] !== '/') {
    throw new PdfStructuralError('invalid', 'Malformed PDF dictionary key')
  }
  const start = ++scanner.position
  while (!isDelimiter(scanner.source[scanner.position])) {
    scanner.position += 1
    ensureDictionaryBound(scanner)
  }
  return decodeName(scanner.source, start, scanner.position)
}

function skipLiteralString(scanner: Scanner): void {
  scanner.position += 1
  let depth = 1
  while (scanner.position < scanner.source.length && depth > 0) {
    const character = scanner.source[scanner.position++]
    scanner.exemptBytes += 1
    ensureDictionaryBound(scanner)
    if (character === '\\') {
      if (scanner.position < scanner.source.length) {
        if (scanner.source[scanner.position] === '\r') {
          scanner.position += 1
          scanner.exemptBytes += 1
        }
        if (scanner.source[scanner.position] === '\n') {
          scanner.position += 1
          scanner.exemptBytes += 1
        } else {
          scanner.position += 1
          scanner.exemptBytes += 1
        }
      }
    } else if (character === '(') {
      depth += 1
    } else if (character === ')') {
      depth -= 1
    }
  }
  if (depth !== 0) throw new PdfStructuralError('invalid', 'Unterminated PDF string')
}

function skipHexString(scanner: Scanner): void {
  scanner.position += 1
  while (scanner.position < scanner.source.length && scanner.source[scanner.position] !== '>') {
    scanner.position += 1
    scanner.exemptBytes += 1
    ensureDictionaryBound(scanner)
  }
  if (scanner.position >= scanner.source.length) {
    throw new PdfStructuralError('invalid', 'Unterminated PDF hex string')
  }
  scanner.position += 1
}

function readRegularToken(scanner: Scanner): string {
  const start = scanner.position
  while (!isDelimiter(scanner.source[scanner.position])) {
    scanner.position += 1
    ensureDictionaryBound(scanner)
  }
  if (start === scanner.position) throw new PdfStructuralError('invalid', 'Malformed PDF token')
  return scanner.source.slice(start, scanner.position)
}

function readArray(scanner: Scanner, depth: number): PdfValue[] {
  const values: PdfValue[] = []
  scanner.position += 1
  while (true) {
    skipTrivia(scanner)
    if (scanner.position >= scanner.source.length) {
      throw new PdfStructuralError('invalid', 'Unterminated PDF array')
    }
    if (scanner.source[scanner.position] === ']') {
      scanner.position += 1
      return values
    }
    values.push(readValue(scanner, depth + 1))
  }
}

function readValue(scanner: Scanner, depth: number): PdfValue {
  if (depth > scanner.limits.nestingDepth) {
    throw new PdfStructuralError('limit', 'PDF dictionary nesting exceeds the supported limit')
  }
  skipTrivia(scanner)
  const character = scanner.source[scanner.position]
  if (character === undefined) throw new PdfStructuralError('invalid', 'Missing PDF dictionary value')
  if (character === '(') {
    skipLiteralString(scanner)
    return { kind: 'complex' }
  }
  if (character === '<' && scanner.source[scanner.position + 1] === '<') {
    readDictionary(scanner, depth + 1)
    return { kind: 'complex' }
  }
  if (character === '<') {
    skipHexString(scanner)
    return { kind: 'complex' }
  }
  if (character === '[') {
    const values = readArray(scanner, depth + 1)
    if (values.every((value) => value.kind === 'name')) {
      return {
        kind: 'name-array',
        values: values.map((value) => value.value),
      }
    }
    return { kind: 'complex' }
  }
  if (character === '/') return { kind: 'name', value: readName(scanner) }

  const token = readRegularToken(scanner)
  if (!/^[+-]?\d+$/.test(token)) return { kind: 'complex' }
  const value = Number(token)
  if (!Number.isSafeInteger(value)) return { kind: 'number', value }

  const afterNumber = scanner.position
  skipTrivia(scanner)
  let generation = ''
  if (!isDelimiter(scanner.source[scanner.position])) generation = readRegularToken(scanner)
  if (/^\d+$/.test(generation)) {
    skipTrivia(scanner)
    const referenceStart = scanner.position
    const referenceToken = !isDelimiter(scanner.source[scanner.position])
      ? readRegularToken(scanner)
      : ''
    if (referenceToken === 'R') {
      return {
        kind: 'reference',
        objectNumber: value,
        generation: Number(generation),
      }
    }
    scanner.position = referenceStart
  }
  scanner.position = afterNumber
  return { kind: 'number', value }
}

function readDictionary(scanner: Scanner, depth = 0): PdfDictionary {
  const start = scanner.position
  if (scanner.source.slice(start, start + 2) !== '<<') {
    throw new PdfStructuralError('invalid', 'Malformed PDF dictionary')
  }
  const parentStart = scanner.dictionaryStart
  const parentExemptStart = scanner.dictionaryExemptStart
  scanner.dictionaryStart = start
  scanner.dictionaryExemptStart = scanner.exemptBytes
  scanner.position += 2
  const entries = new Map<string, PdfValue>()
  try {
    while (true) {
      skipTrivia(scanner)
      if (scanner.source.slice(scanner.position, scanner.position + 2) === '>>') {
        scanner.position += 2
        return { end: scanner.position, entries }
      }
      if (scanner.position >= scanner.source.length) {
        throw new PdfStructuralError('invalid', 'Unterminated PDF dictionary')
      }
      const key = readName(scanner)
      const value = readValue(scanner, depth + 1)
      if (entries.has(key) && ['Type', 'N', 'First', 'Length', 'Size'].includes(key)) {
        throw new PdfStructuralError('invalid', 'Ambiguous PDF stream declaration')
      }
      entries.set(key, value)
    }
  } finally {
    scanner.dictionaryStart = parentStart
    scanner.dictionaryExemptStart = parentExemptStart
  }
}

function numberEntry(dictionary: PdfDictionary, key: string): number | undefined {
  const entry = dictionary.entries.get(key)
  return entry?.kind === 'number' ? entry.value : undefined
}

function skipSourceTrivia(source: string, initialPosition: number): number {
  let position = initialPosition
  while (position < source.length) {
    if (isWhitespace(source[position])) {
      position += 1
      continue
    }
    if (source[position] !== '%') return position
    while (position < source.length && !['\r', '\n'].includes(source[position] ?? '')) {
      position += 1
    }
  }
  return position
}

interface StreamPayload {
  end: number
  dataStart: number
  length?: number
}

function skipStreamPayload(
  source: string,
  dictionary: PdfDictionary,
  objectStream: boolean,
  limits: PdfStructuralLimits,
  integerObjects?: ReadonlyMap<string, number>,
): StreamPayload | undefined {
  const streamKeyword = skipSourceTrivia(source, dictionary.end)
  if (
    source.slice(streamKeyword, streamKeyword + 6) !== 'stream' ||
    !isDelimiter(source[streamKeyword + 6])
  ) {
    return undefined
  }
  let dataStart = streamKeyword + 6
  if (source[dataStart] === '\r') dataStart += 1
  if (source[dataStart] === '\n') dataStart += 1

  const lengthEntry = dictionary.entries.get('Length')
  if (lengthEntry?.kind === 'reference') {
    if (objectStream) {
      throw new PdfStructuralError('invalid', 'Object stream requires a direct bounded length')
    }
    const resolvedLength = integerObjects?.get(
      `${lengthEntry.objectNumber}:${lengthEntry.generation}`,
    )
    if (resolvedLength === undefined) {
      throw new PdfStructuralError('invalid', 'Indirect stream length is not resolvable from xref')
    }
    if (
      !Number.isSafeInteger(resolvedLength) ||
      resolvedLength < 0 ||
      resolvedLength > limits.declaredStreamBytes
    ) {
      throw new PdfStructuralError('limit', 'Suspicious indirect stream length declaration')
    }
    const dataEnd = dataStart + resolvedLength
    const endMarker = skipSourceTrivia(source, dataEnd)
    if (
      dataEnd > source.length ||
      source.slice(endMarker, endMarker + 9) !== 'endstream' ||
      !isDelimiter(source[endMarker + 9])
    ) {
      throw new PdfStructuralError('invalid', 'Indirect PDF stream length does not reach endstream')
    }
    return { end: endMarker + 9, dataStart, length: resolvedLength }
  }
  if (lengthEntry?.kind !== 'number') {
    throw new PdfStructuralError('invalid', 'PDF stream requires a bounded length')
  }
  const length = lengthEntry.value

  const dataEnd = dataStart + length
  if (dataEnd > source.length) throw new PdfStructuralError('invalid', 'PDF stream exceeds file bounds')
  const endMarker = skipSourceTrivia(source, dataEnd)
  if (
    source.slice(endMarker, endMarker + 9) !== 'endstream' ||
    !isDelimiter(source[endMarker + 9])
  ) {
    throw new PdfStructuralError('invalid', 'PDF stream length does not reach endstream')
  }
  return { end: endMarker + 9, dataStart, length }
}

function skipTopLevelLiteralString(source: string, initialPosition: number): number {
  let position = initialPosition + 1
  let depth = 1
  while (position < source.length && depth > 0) {
    const character = source[position++]
    if (character === '\\') {
      if (source[position] === '\r') position += 1
      if (source[position] === '\n') position += 1
      else if (position < source.length) position += 1
    } else if (character === '(') {
      depth += 1
    } else if (character === ')') {
      depth -= 1
    }
  }
  if (depth !== 0) throw new PdfStructuralError('invalid', 'Unterminated PDF string')
  return position
}

function skipTopLevelHexString(source: string, initialPosition: number): number {
  const end = source.indexOf('>', initialPosition + 1)
  if (end < 0) throw new PdfStructuralError('invalid', 'Unterminated PDF hex string')
  return end + 1
}

function readTopLevelToken(source: string, initialPosition: number): [string, number] {
  let position = initialPosition
  while (!isDelimiter(source[position])) position += 1
  return [source.slice(initialPosition, position), position]
}

function nameEntry(dictionary: PdfDictionary, key: string): string | undefined {
  const entry = dictionary.entries.get(key)
  return entry?.kind === 'name' ? entry.value : undefined
}

function filterEntries(dictionary: PdfDictionary): string[] {
  const entry = dictionary.entries.get('Filter')
  if (entry === undefined) return []
  if (entry?.kind === 'name') return [entry.value]
  if (entry?.kind === 'name-array') return entry.values
  return ['__unsupported__']
}

interface StructurePass {
  streams: PdfStructuralStream[]
}

function readUnsignedToken(source: string, position: number): [number, number] {
  const tokenStart = skipSourceTrivia(source, position)
  const [token, tokenEnd] = readTopLevelToken(source, tokenStart)
  if (!/^\d{1,12}$/.test(token)) {
    throw new PdfStructuralError('invalid', 'Malformed classic xref declaration')
  }
  const value = Number(token)
  if (!Number.isSafeInteger(value)) {
    throw new PdfStructuralError('invalid', 'Malformed classic xref declaration')
  }
  return [value, tokenEnd]
}

function finalStartXref(source: string): number | undefined {
  const eof = source.lastIndexOf('%%EOF')
  if (eof < 0) return undefined
  const marker = source.lastIndexOf('startxref', eof)
  if (marker < 0) return undefined
  const declaration = source.slice(marker + 9, eof).trim()
  if (!/^\d{1,12}$/.test(declaration)) return undefined
  const offset = Number(declaration)
  return Number.isSafeInteger(offset) && offset < source.length ? offset : undefined
}

function integerObjectAtXrefOffset(
  source: string,
  offset: number,
  expectedObject: number,
  expectedGeneration: number,
): number | undefined {
  let position = skipSourceTrivia(source, offset)
  const [objectNumber, objectEnd] = readUnsignedToken(source, position)
  const [generation, generationEnd] = readUnsignedToken(source, objectEnd)
  position = skipSourceTrivia(source, generationEnd)
  const [keyword, keywordEnd] = readTopLevelToken(source, position)
  if (
    objectNumber !== expectedObject ||
    generation !== expectedGeneration ||
    keyword !== 'obj'
  ) {
    throw new PdfStructuralError('invalid', 'xref entry does not point to its indirect object')
  }
  position = skipSourceTrivia(source, keywordEnd)
  const [valueToken, valueEnd] = readTopLevelToken(source, position)
  if (!/^[+-]?\d+$/.test(valueToken)) return undefined
  const value = Number(valueToken)
  if (!Number.isSafeInteger(value)) return undefined
  position = skipSourceTrivia(source, valueEnd)
  const [endKeyword] = readTopLevelToken(source, position)
  return endKeyword === 'endobj' ? value : undefined
}

function discoverIntegerObjectsFromXref(
  source: string,
  limits: PdfStructuralLimits,
): Map<string, number> {
  const initialOffset = finalStartXref(source)
  if (initialOffset === undefined) return new Map()
  const entries = new Map<string, { offset: number; objectNumber: number; generation: number }>()
  const visited = new Set<number>()
  let xrefOffset: number | undefined = initialOffset
  while (xrefOffset !== undefined) {
    if (visited.has(xrefOffset) || visited.size > limits.incrementalUpdates) {
      throw new PdfStructuralError('limit', 'Invalid or excessive xref chain')
    }
    visited.add(xrefOffset)
    let position = skipSourceTrivia(source, xrefOffset)
    const [xrefKeyword, xrefEnd] = readTopLevelToken(source, position)
    if (xrefKeyword !== 'xref') {
      return new Map()
    }
    position = xrefEnd
    while (true) {
      position = skipSourceTrivia(source, position)
      if (source.slice(position, position + 7) === 'trailer' && isDelimiter(source[position + 7])) {
        position += 7
        break
      }
      const [firstObject, firstEnd] = readUnsignedToken(source, position)
      const [count, countEnd] = readUnsignedToken(source, firstEnd)
      if (count > limits.declaredObjectSlots) {
        throw new PdfStructuralError('limit', 'Suspicious xref subsection')
      }
      position = countEnd
      for (let index = 0; index < count; index += 1) {
        const [offset, offsetEnd] = readUnsignedToken(source, position)
        const [generation, generationEnd] = readUnsignedToken(source, offsetEnd)
        position = skipSourceTrivia(source, generationEnd)
        const [state, stateEnd] = readTopLevelToken(source, position)
        if (state !== 'n' && state !== 'f') {
          throw new PdfStructuralError('invalid', 'Malformed classic xref entry')
        }
        if (state === 'n') {
          const objectNumber = firstObject + index
          const key = `${objectNumber}:${generation}`
          if (!entries.has(key)) entries.set(key, { offset, objectNumber, generation })
        }
        position = stateEnd
      }
    }
    position = skipSourceTrivia(source, position)
    if (source.slice(position, position + 2) !== '<<') {
      throw new PdfStructuralError('invalid', 'Classic xref trailer is missing')
    }
    const scanner: Scanner = {
      source,
      position,
      limits,
      dictionaryStart: position,
      dictionaryExemptStart: 0,
      exemptBytes: 0,
    }
    const trailer = readDictionary(scanner)
    xrefOffset = numberEntry(trailer, 'Prev')
  }

  const integers = new Map<string, number>()
  for (const [key, entry] of entries) {
    const value = integerObjectAtXrefOffset(
      source,
      entry.offset,
      entry.objectNumber,
      entry.generation,
    )
    if (value !== undefined) integers.set(key, value)
  }
  return integers
}

function inspectStructure(
  source: string,
  limits: PdfStructuralLimits,
  knownIntegerObjects: ReadonlyMap<string, number>,
): StructurePass {
  let position = 0
  let objectStreams = 0
  let aggregateObjects = 0
  let aggregateLength = 0
  let sizeDeclarations = 0
  let startxrefDeclarations = 0
  let indirectObjects = 0
  let currentObject: { objectNumber: number; generation: number; hasValue: boolean } | null = null
  const streams: PdfStructuralStream[] = []
  while (position < source.length) {
    position = skipSourceTrivia(source, position)
    if (position >= source.length) break
    if (source[position] === '(') {
      position = skipTopLevelLiteralString(source, position)
      continue
    }
    if (source[position] === '<' && source[position + 1] !== '<') {
      position = skipTopLevelHexString(source, position)
      continue
    }
    if (source[position] === '/') {
      position += 1
      while (!isDelimiter(source[position])) position += 1
      continue
    }
    if (source.slice(position, position + 2) !== '<<') {
      if (isDelimiter(source[position])) {
        position += 1
        continue
      }
      const [token, tokenEnd] = readTopLevelToken(source, position)
      position = tokenEnd
      if (/^\d{1,12}$/.test(token) && currentObject === null) {
        const generationStart = skipSourceTrivia(source, position)
        const [generationToken, generationEnd] = readTopLevelToken(source, generationStart)
        const objectKeywordStart = skipSourceTrivia(source, generationEnd)
        const [objectKeyword, objectKeywordEnd] = readTopLevelToken(source, objectKeywordStart)
        if (/^\d{1,5}$/.test(generationToken) && objectKeyword === 'obj') {
          const objectNumber = Number(token)
          const generation = Number(generationToken)
          if (!Number.isSafeInteger(objectNumber) || !Number.isSafeInteger(generation)) {
            throw new PdfStructuralError('invalid', 'Invalid indirect object declaration')
          }
          currentObject = { objectNumber, generation, hasValue: false }
          indirectObjects += 1
          if (indirectObjects > limits.indirectObjects) {
            throw new PdfStructuralError('limit', 'Too many indirect PDF objects')
          }
          position = objectKeywordEnd
          continue
        }
      }
      if (token === 'endobj') {
        if (currentObject === null) {
          throw new PdfStructuralError('invalid', 'Unexpected endobj declaration')
        }
        currentObject = null
        continue
      }
      if (currentObject !== null && !currentObject.hasValue) {
        currentObject.hasValue = true
      }
      if (token !== 'startxref') continue
      startxrefDeclarations += 1
      if (startxrefDeclarations > limits.incrementalUpdates) {
        throw new PdfStructuralError('limit', 'Too many incremental PDF updates')
      }
      position = skipSourceTrivia(source, position)
      const [offsetToken, offsetEnd] = readTopLevelToken(source, position)
      if (!/^\d{1,12}$/.test(offsetToken)) {
        throw new PdfStructuralError('invalid', 'Invalid startxref declaration')
      }
      const offset = Number(offsetToken)
      if (!Number.isSafeInteger(offset) || offset >= source.length) {
        throw new PdfStructuralError('invalid', 'Invalid startxref declaration')
      }
      position = offsetEnd
      continue
    }

    const dictionaryStart = position
    const scanner: Scanner = {
      source,
      position: dictionaryStart,
      limits,
      dictionaryStart,
      dictionaryExemptStart: 0,
      exemptBytes: 0,
    }
    const dictionary = readDictionary(scanner)
    position = dictionary.end
    if (currentObject !== null) currentObject.hasValue = true

    const sizeEntry = dictionary.entries.get('Size')
    if (sizeEntry !== undefined) {
      sizeDeclarations += 1
      if (sizeDeclarations > limits.incrementalUpdates + 1) {
        throw new PdfStructuralError('limit', 'Too many xref size declarations')
      }
      if (
        sizeEntry.kind !== 'number' ||
        !Number.isSafeInteger(sizeEntry.value) ||
        sizeEntry.value <= 0 ||
        sizeEntry.value > limits.declaredObjectSlots
      ) {
        throw new PdfStructuralError('limit', 'Suspicious xref size declaration')
      }
    }

    const lengthEntry = dictionary.entries.get('Length')
    if (lengthEntry?.kind === 'number') {
      const length = lengthEntry.value
      if (!Number.isSafeInteger(length) || length < 0 || length > limits.declaredStreamBytes) {
        throw new PdfStructuralError('limit', 'Suspicious stream length declaration')
      }
      aggregateLength += length
      if (aggregateLength > limits.aggregateDeclaredStreamBytes) {
        throw new PdfStructuralError('limit', 'Aggregate stream declarations exceed the supported limit')
      }
    }

    const type = dictionary.entries.get('Type')
    const objectStream = type?.kind === 'name' && type.value === 'ObjStm'
    const stream = skipStreamPayload(
      source,
      dictionary,
      objectStream,
      limits,
      knownIntegerObjects,
    )
    if (stream !== undefined) {
      if (currentObject === null) {
        throw new PdfStructuralError('invalid', 'PDF stream is not bound to an indirect object')
      }
      position = stream.end
      if (
        lengthEntry?.kind === 'reference' &&
        stream.length !== undefined
      ) {
        aggregateLength += stream.length
        if (aggregateLength > limits.aggregateDeclaredStreamBytes) {
          throw new PdfStructuralError(
            'limit',
            'Aggregate stream declarations exceed the supported limit',
          )
        }
      }
      if (stream.length !== undefined) {
        streams.push({
          dataStart: stream.dataStart,
          length: stream.length,
          filters: filterEntries(dictionary),
          type: nameEntry(dictionary, 'Type'),
          subtype: nameEntry(dictionary, 'Subtype'),
          typeIsIndirect: dictionary.entries.get('Type')?.kind === 'reference',
          subtypeIsIndirect: dictionary.entries.get('Subtype')?.kind === 'reference',
          width: numberEntry(dictionary, 'Width'),
          height: numberEntry(dictionary, 'Height'),
        })
      }
    }
    if (!objectStream) continue
    objectStreams += 1
    if (objectStreams > limits.objectStreams) {
      throw new PdfStructuralError('limit', 'Too many object streams')
    }

    const count = numberEntry(dictionary, 'N')
    const first = numberEntry(dictionary, 'First')
    const length = numberEntry(dictionary, 'Length')
    if (count === undefined || first === undefined || length === undefined) {
      throw new PdfStructuralError('invalid', 'Malformed object stream declaration')
    }
    if (
      !Number.isSafeInteger(count) ||
      count <= 0 ||
      count > limits.objectsPerStream ||
      !Number.isSafeInteger(first) ||
      first < 0 ||
      first > limits.declaredStreamBytes
    ) {
      throw new PdfStructuralError('limit', 'Suspicious object stream declaration')
    }
    aggregateObjects += count
    if (aggregateObjects > limits.indirectObjects) {
      throw new PdfStructuralError('limit', 'Object stream contents exceed the supported limit')
    }
  }
  if (currentObject !== null) throw new PdfStructuralError('invalid', 'Unterminated indirect object')

  return { streams }
}

export function preflightPdfStructure(
  bytes: Uint8Array,
  limits: PdfStructuralLimits = DEFAULT_LIMITS,
): PdfStructuralInspection {
  if (bytes.byteLength === 0 || bytes.byteLength > limits.fileBytes) {
    throw new PdfStructuralError('limit', 'File size is outside the supported limit')
  }
  const decoder = new TextDecoder('latin1')
  const header = decoder.decode(bytes.subarray(0, Math.min(bytes.length, 1024)))
  if (!header.includes('%PDF-')) throw new PdfStructuralError('invalid', 'Missing PDF header')

  const source = decoder.decode(bytes)
  const integerObjects = discoverIntegerObjectsFromXref(source, limits)
  const inspection = inspectStructure(source, limits, integerObjects)
  return { streams: inspection.streams }
}
